import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a <theme-drawer>.
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** Minimum total number of pieces required */
  #minimumOrderQuantity = 10;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    this.#updateMinimumOrderState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Handles the cart drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#updateMinimumOrderState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Handles cart line updates.
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#updateMinimumOrderState();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Updates the minimum order checkout state.
   */
  #updateMinimumOrderState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const checkoutButton = dialog.querySelector('#checkout');

    if (!checkoutButton) return;

    /*
     * Shopify cart.item_count includes the total quantity
     * of all products in the cart.
     *
     * Example:
     * 3 shirts + 2 jeans + 5 jackets = 10 pieces.
     */

    const cartItems = dialog.querySelectorAll(
      '[data-cart-item]'
    );

    let totalQuantity = 0;

    cartItems.forEach((item) => {
      const quantityInput = item.querySelector(
        'input[name="updates[]"]'
      );

      if (quantityInput) {
        const quantity = parseInt(
          quantityInput.value,
          10
        );

        if (!Number.isNaN(quantity)) {
          totalQuantity += quantity;
        }
      }
    });

    /*
     * If the cart item structure does not expose
     * quantity inputs, use Shopify's rendered
     * cart count if available.
     */
    if (totalQuantity === 0 && !this.#isCartEmpty()) {
      const quantityInputs = dialog.querySelectorAll(
        'input[name="updates[]"]'
      );

      quantityInputs.forEach((input) => {
        const quantity = parseInt(
          input.value,
          10
        );

        if (!Number.isNaN(quantity)) {
          totalQuantity += quantity;
        }
      });
    }

    const remaining =
      this.#minimumOrderQuantity - totalQuantity;

    if (totalQuantity < this.#minimumOrderQuantity) {
      checkoutButton.disabled = true;
      checkoutButton.setAttribute(
        'aria-disabled',
        'true'
      );

      const buttonText =
        checkoutButton.querySelector('.button-text');

      if (buttonText) {
        buttonText.textContent =
          `Add ${remaining} more`;
      }
    } else {
      checkoutButton.disabled = false;
      checkoutButton.removeAttribute(
        'aria-disabled'
      );

      const buttonText =
        checkoutButton.querySelector('.button-text');

      if (buttonText) {
        buttonText.textContent = 'Checkout';
      }
    }

    /*
     * Hide accelerated checkout buttons until
     * minimum quantity is reached.
     */
    const acceleratedButtons =
      dialog.querySelector(
        '.additional-checkout-buttons'
      );

    if (acceleratedButtons) {
      acceleratedButtons.style.display =
        totalQuantity >= this.#minimumOrderQuantity
          ? ''
          : 'none';
    }
  }

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content =
      dialog.querySelector('.__content');

    const summary =
      dialog.querySelector('.__summary');

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('-component')) {
  customElements.define(
    '-component',
    CartDrawerComponent
  );
}